import styled from "@emotion/styled";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { TenantRole } from "graphqlBase/types";
import { dummyIdPrefix } from "pages/UploadCertificate/config";
import {
  getFileTypeIdForFileTypeName as standardGetFileTypeId,
  uploadCertificateFilesSelectorFamily as standardUploadCertificateFilesSelectorFamily,
} from "pages/UploadCertificate/state/uploadCertificateFileState";
import {
  getFileTypeIdForFileTypeName as gridFormingGetFileTypeId,
  uploadCertificateFilesSelectorFamily as gridFormingUploadCertificateFilesSelectorFamily,
} from "pages/UploadGridFormingCertificate/state/uploadCertificateFileState";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useRecoilState } from "recoil";
import ordinaryColors from "themes/ordinaryColors";
import translations from "translations";
import { v4 as uuidv4 } from "uuid";
import { toBase64 } from "./FileUtils";
import Props from "./index.d";
import UploadedFileIcon from "./UploadedFileIcon";
import { useGetUserInfo, useHasFileVisibilityPermission } from "lib/userInfo";
import { showNoteOnCarefulInputConditionally } from "pages/UploadCertificate/utils";
import FileVisibilityModal from "../CertificateFileUploadMulti/FileVisibilityModal";
import { UploadCertificateFile } from "pages/UploadCertificate/types";

export const titleFileStyle = {
  width: "20rem",
  color: ordinaryColors.lightTextPrimary,
  fontFeatureSettings: "'clig' off, 'liga' off",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "175% /* 28px */",
  letterSpacing: "0.15px",
};
export const StyledUploadButton = styled(Button)(({ theme }) => ({
  height: "43px",
  width: "100%",
  borderRadius: "0px",
  variant: "outlined",
  border: "1px solid",
  borderColor: ordinaryColors.functionalBlack,
  minWidth: "64px",
  color: ordinaryColors.functionalBlack,
}));

const CertificateFileUpload: React.FC<Props> = (props) => {
  const { fileType, title, formVersion, testId } = props;

  // Determine which state management to use based on formVersion
  const isGridForming = formVersion === "matrixCertificateCreate";
  const uploadCertificateFilesSelectorFamily = isGridForming
    ? gridFormingUploadCertificateFilesSelectorFamily
    : standardUploadCertificateFilesSelectorFamily;
  const getFileTypeIdForFileTypeName = isGridForming ? gridFormingGetFileTypeId : standardGetFileTypeId;
  const [fileToUpload, setFileToUpload] = useRecoilState(uploadCertificateFilesSelectorFamily(fileType));
  const [showFileSizeWarning, setShowFileSizeWarning] = useState(false);
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);
  const userInfo = useGetUserInfo();
  const isPlatformOperator = userInfo?.role === "PLATFORM_OPERATOR";
  const hasFileVisibilityPermission = useHasFileVisibilityPermission();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const certificateFileName = file.name;
      const certificateFileTypeId = getFileTypeIdForFileTypeName(fileType);
      const certificateId = `${dummyIdPrefix}${uuidv4()}`;

      if (file.size > 20000000) {
        setShowFileSizeWarning(true);
        return;
      }
      toBase64([file]).then((base64FileString) => {
        setFileToUpload({
          certificateFileName,
          certificateFileTypeId,
          certificateId,
          base64FileString,
          size: file.size,
        });
      });
    },
    [fileType, setFileToUpload, getFileTypeIdForFileTypeName]
  );

  const removeFile = () => () => {
    setFileToUpload(undefined);
  };

  const hasVisibilityGrants = (file: UploadCertificateFile) =>
    (file.grantAccessToRoles?.length ?? 0) > 0 || (file.grantAccessToTenantIds?.length ?? 0) > 0;

  const getVisibilityTooltip = (file: UploadCertificateFile) => {
    if (!hasVisibilityGrants(file)) {
      return translations.pages.certificateUpload.fileVisibility.configureAccess;
    }
    const roleCount = file.grantAccessToRoles?.length ?? 0;
    const tenantCount = file.grantAccessToTenantIds?.length ?? 0;
    return `${translations.pages.certificateUpload.fileVisibility.accessGrantedTo} ${roleCount} ${
      roleCount === 1 ? "role" : "roles"
    }, ${tenantCount} ${tenantCount === 1 ? "tenant" : "tenants"}`;
  };

  const handleVisibilitySave = (file: UploadCertificateFile, roles: TenantRole[], tenantIds: string[]) => {
    // Use empty arrays to clear visibility - backend expects [] to clear, not null/undefined
    setFileToUpload({
      ...file,
      grantAccessToRoles: roles,
      grantAccessToTenantIds: tenantIds,
    });
  };

  const { open, getInputProps } = useDropzone({
    onDrop,
    noDrag: true,
    accept: props.mimeTypes.reduce((acc, mimeType) => ({ ...acc, [mimeType]: [] }), {}),
    multiple: false,
    useFsAccessApi: false,
  });

  if (fileToUpload)
    return (
      <Box>
        <FileVisibilityModal
          open={openVisibilityModal}
          file={fileToUpload}
          onClose={() => setOpenVisibilityModal(false)}
          onSave={handleVisibilitySave}
        />
        <Box sx={{ display: "flex" }}>
          <>
            <input {...getInputProps()} />
            <UploadedFileIcon {...fileToUpload} />
            <Box sx={{ display: "flex" }}>
              <Typography sx={titleFileStyle}>{fileToUpload.certificateFileName}</Typography>
            </Box>
          </>
        </Box>
        {/* Only render this container if there's something to show inside */}
        {/* Container shows if: visibility icon will show OR remove button will show */}
        {((!fileToUpload.id &&
          fileType !== "Cover Sheet" &&
          fileType !== "Perturbations Test Report" &&
          hasFileVisibilityPermission) ||
          (fileType !== "Cover Sheet" && fileType !== "Perturbations Test Report") ||
          !fileToUpload.id ||
          isPlatformOperator) && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "-40px", gap: 0 }}>
            {!fileToUpload.id &&
              fileType !== "Cover Sheet" &&
              fileType !== "Perturbations Test Report" &&
              hasFileVisibilityPermission && (
                <Tooltip title={getVisibilityTooltip(fileToUpload)}>
                  <IconButton onClick={() => setOpenVisibilityModal(true)}>
                    {hasVisibilityGrants(fileToUpload) ? (
                      <VisibilityIcon sx={{ color: "primary.main" }} />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            {/* Only restrict removal for mandatory files (Cover Sheet, Perturbations Test Report) */}
            {(fileType !== "Cover Sheet" && fileType !== "Perturbations Test Report") ||
            !fileToUpload.id ||
            isPlatformOperator ? (
              <IconButton onClick={removeFile()}>
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>
        )}
      </Box>
    );
  else
    return (
      <>
        {showNoteOnCarefulInputConditionally({
          condition: !isPlatformOperator && (fileType === "Cover Sheet" || fileType === "Perturbations Test Report"),
        })}
        {showFileSizeWarning && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <span style={{ color: ordinaryColors.error }}>
              {translations.pages.certificateUpload.certificateFileTooLarge}
            </span>
            <IconButton onClick={() => setShowFileSizeWarning(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        {!showFileSizeWarning && (
          <StyledUploadButton variant="outlined" onClick={open} startIcon={<AttachFileIcon />}>
            <input {...getInputProps()} data-testid={testId} />
            {title ?? translations.globals.button.uploadGridConnectionValues}
          </StyledUploadButton>
        )}
      </>
    );
};
export default CertificateFileUpload;
